import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip, { ChipProps } from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { Flags } from 'react-feature-flags';
import ParticipantActionMenu, { ParticipantAction } from '../ParticipantActionMenu';
import { FEATURES } from '../../../../../../../../core/constants/features.constants';

interface ParticipantSummaryProps {
  firstName: string | null;
  surname: string | null;
  status: string | null;
  hasError?: boolean;
  participantRef: string | null;
  onAction: (action: ParticipantAction) => void;
}

function getInitials(firstName: string | null, surname: string | null) {
  const firstInitial = firstName?.trim()?.[0] ?? '';
  const surnameInitial = surname?.trim()?.[0] ?? '';

  return `${firstInitial}${surnameInitial}`.toUpperCase() || '?';
}

function getStatusColour(hasError: boolean): ChipProps['color'] {
  return hasError ? 'error' : 'success';
}

function ParticipantSummary({
  status,
  hasError = false,
  firstName,
  surname,
  participantRef,
  onAction,
}: ParticipantSummaryProps) {
  const initials = getInitials(firstName, surname);
  const displayName = [firstName, surname].filter(Boolean).join(' ') || 'Unknown participant';
  const displayStatus = status || 'UNKNOWN';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        mb: 2,
        minWidth: 0,
      }}
    >
      <Avatar
        sx={{
          width: 86,
          height: 86,
          bgcolor: 'primary.main',
          fontSize: '1.8rem',
          fontWeight: 600,
          flexShrink: 0,
        }}
      >
        {initials}
      </Avatar>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          minWidth: 0,
        }}
      >
        <Chip label={displayStatus} color={getStatusColour(hasError)} sx={{ mb: 0.75 }} />

        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'flex-start',
            gap: 1,
            maxWidth: '100%',
          }}
        >
          <Typography
            variant="h4"
            component="h2"
            sx={{
              lineHeight: 1,
              overflowWrap: 'anywhere',
              minWidth: 0,
            }}
          >
            {displayName}
          </Typography>
          <Flags authorizedFlags={[FEATURES.PARTICIPANT_EDIT]}>
            <Box sx={{ flexShrink: 0, mt: -0.5 }}>
              <ParticipantActionMenu onAction={onAction} />
            </Box>
          </Flags>
        </Box>

        <Typography
          variant="h6"
          component="p"
          color="text.secondary"
          sx={{
            lineHeight: 1.2,
            mt: 0.5,
            overflowWrap: 'anywhere',
          }}
        >
          {participantRef || 'No participant reference'}
        </Typography>
      </Box>
    </Box>
  );
}

export default ParticipantSummary;
